import React, { useState, useEffect } from "react";
import MainDashboardLayout from "../../../../Layout/MainDashboardLayout";
import GRISidebar from "../../../../Layout/GRISidebar";
import styles from "../../../../styles/gri/general.module.css";
import { Box, Button, Alert, Snackbar } from "@mui/material";
import EastIcon from "@mui/icons-material/East";
import { put, api, get } from "../../../../utils/api";
import { useNavigate, useParams } from "react-router-dom";
import SectionHeading from "../../GRI300/GRI308/SectionHeading";
import CustomTextField from "../../GRI300/GRI308/CustomTextField";
import ReviewBox from "../../../../Components/Common/ReviewBox";

import { currentYear } from "../../../../utils/generateYearRanges";

const GRI413_1 = ({ hide }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [disabled, setDisabled] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackOpen, setSnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [formData, setFormData] = useState({
    evaluated: { percentage: "", remark: "" },
    identifiedSuppliers: { percentage: "", remark: "" },
    negativeSocialImpactsSupplyChain: { percentage: "", remark: "" },
    supplierAgreementRate: { percentage: "", remark: "" },
    supplierTerminationRate: { percentage: "", remark: "" },
  });
  const [griReview, setGriReview] = useState(() => JSON.parse(localStorage.getItem("griReview")));

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await get(`/gri/get?type=GRI400&Id=${id}`);
        if (response?.data?.data?.year < currentYear - 1)
          setDisabled(true);

        const data = response.data.data.GRI400?.negativeSocialImpact || {};
        setFormData({
          evaluated: {
            percentage: data.evaluated?.percentage || "",
            remark: data.evaluated?.remark || "",
          },
          identifiedSuppliers: {
            percentage: data.identifiedSuppliers?.percentage || "",
            remark: data.identifiedSuppliers?.remark || "",
          },
          negativeSocialImpactsSupplyChain: {
            percentage: data.negativeSocialImpactsSupplyChain?.percentage || "",
            remark: data.negativeSocialImpactsSupplyChain?.remark || "",
          },
          supplierAgreementRate: {
            percentage: data.supplierAgreementRate?.percentage || "",
            remark: data.supplierAgreementRate?.remark || "",
          },
          supplierTerminationRate: {
            percentage: data.supplierTerminationRate?.percentage || "",
            remark: data.supplierTerminationRate?.remark || "",
          },
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleStorage = () => {
      setGriReview(JSON.parse(localStorage.getItem("griReview")));
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);

  useEffect(() => {
    setGriReview(JSON.parse(localStorage.getItem("griReview")));
  }, []);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleNestedInputChange = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));

    // Construct the correct error key
    const errorKey = `${section}.${field}`;

    // Remove error for the specific field if the value is valid
    setErrors((prev) => {
      if (value.trim()) {
        const { [errorKey]: removedError, ...rest } = prev; // Remove the specific error key
        return rest;
      }
      return prev;
    });
  };

  const validateForm = () => {
    let newErrors = {};
    let formIsValid = true;

    // List of fields to validate
    const fieldsToValidate = [
      //   "identificationAndImplementationProcedure",
      "evaluated",
      "identifiedSuppliers",
      "negativeSocialImpactsSupplyChain",
      "supplierAgreementRate",
      "supplierTerminationRate",
    ];

    // Loop through each field and apply validation
    fieldsToValidate.forEach((field) => {
      const percentage = formData[field].percentage;
      const remark = formData[field].remark;

      // if (!percentage) {
      //   newErrors[`${field}.percentage`] = "This field is required";
      //   formIsValid = false;
      // } else
      if (isNaN(percentage)) {
        newErrors[`${field}.percentage`] = "Please enter a valid numeric value";
        formIsValid = false;
      } else if (percentage < 0 || percentage > 100) {
        newErrors[`${field}.percentage`] =
          "Please enter a value between 0 and 100";
        formIsValid = false;
      }

      // if (!remark) {
      //   newErrors[`${field}.remark`] = "This field is required";
      //   formIsValid = false;
      // }
    });

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!disabled) {
      if (!validateForm()) return; // Validate form before submission

      const payload = {
        type: "GRI400",
        submitted414_2: true,
        negativeSocialImpact: {
          ...formData,
        },
      };

      try {
        const token = sessionStorage.getItem("token");
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        await put(`/gri/update?Id=${id}`, payload);
        navigate(`/gri/gri400/415-1/${id}`);
      } catch (error) {
        setSnackOpen({
          open: true,
          message: "Error updating data. Please try again.",
          severity: "error",
        });
      }
    } else {
      navigate(`/gri/gri400/415-1/${id}`);
    }
  };

  const renderContent = ({ hide }) => (
    <>
      {!hide && (
        <p className={styles.section_heading}>
          414 : Supplier Social Assessment
        </p>
      )}
      <p
        className={styles.main_heading}
        style={{ marginBottom: hide ? "-1rem" : "0" }}
      >
        414-2 : Negative social impacts in the supply chain and actions taken
      </p>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* Section A */}

        <Box>
          <SectionHeading text="a. How many suppliers were evaluated for social impacts?" />
          <Box
            sx={{
              display: "flex",
              width: "100%",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "15%" }}>
              <CustomTextField
                value={formData.evaluated.percentage}
                onChange={(e) =>
                  handleNestedInputChange(
                    "evaluated",
                    "percentage",
                    e.target.value
                  )
                }
                placeholder={"%"}
                error={!!errors["evaluated.percentage"]}
                helperText={errors["evaluated.percentage"]}
                disabled={disabled}
              />
            </Box>

            <Box sx={{ width: "85%" }}>
              <CustomTextField
                value={formData.evaluated.remark}
                onChange={(e) =>
                  handleNestedInputChange("evaluated", "remark", e.target.value)
                }
                error={!!errors["evaluated.remark"]}
                helperText={errors["evaluated.remark"]}
                disabled={disabled}
              />
            </Box>
          </Box>

          <SectionHeading text="b. How many suppliers were identified as having significant actual and potential negative social impacts?" />
          <Box
            sx={{
              display: "flex",
              width: "100%",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "15%" }}>
              <CustomTextField
                value={formData.identifiedSuppliers.percentage}
                onChange={(e) =>
                  handleNestedInputChange(
                    "identifiedSuppliers",
                    "percentage",
                    e.target.value
                  )
                }
                placeholder={"%"}
                error={!!errors["identifiedSuppliers.percentage"]}
                helperText={errors["identifiedSuppliers.percentage"]}
                disabled={disabled}
              />
            </Box>

            <Box sx={{ width: "85%" }}>
              <CustomTextField
                value={formData.identifiedSuppliers.remark}
                onChange={(e) =>
                  handleNestedInputChange(
                    "identifiedSuppliers",
                    "remark",
                    e.target.value
                  )
                }
                error={!!errors["identifiedSuppliers.remark"]}
                helperText={errors["identifiedSuppliers.remark"]}
                disabled={disabled}
              />
            </Box>
          </Box>

          <SectionHeading text="c. What significant actual and potential negative social impacts were identified within the supply chain?" />
          <Box
            sx={{
              display: "flex",
              width: "100%",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "15%" }}>
              <CustomTextField
                value={formData.negativeSocialImpactsSupplyChain.percentage}
                onChange={(e) =>
                  handleNestedInputChange(
                    "negativeSocialImpactsSupplyChain",
                    "percentage",
                    e.target.value
                  )
                }
                placeholder={"%"}
                error={!!errors["negativeSocialImpactsSupplyChain.percentage"]}
                helperText={
                  errors["negativeSocialImpactsSupplyChain.percentage"]
                }
                disabled={disabled}
              />
            </Box>

            <Box sx={{ width: "85%" }}>
              <CustomTextField
                value={formData.negativeSocialImpactsSupplyChain.remark}
                onChange={(e) =>
                  handleNestedInputChange(
                    "negativeSocialImpactsSupplyChain",
                    "remark",
                    e.target.value
                  )
                }
                error={!!errors["negativeSocialImpactsSupplyChain.remark"]}
                helperText={errors["negativeSocialImpactsSupplyChain.remark"]}
                disabled={disabled}
              />
            </Box>
          </Box>

          <SectionHeading text="d. What percentage of suppliers identified as having significant actual and potential negative social impacts agreed upon improvements as a result of assessment?" />
          <Box
            sx={{
              display: "flex",
              width: "100%",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "15%" }}>
              <CustomTextField
                value={formData.supplierAgreementRate.percentage}
                onChange={(e) =>
                  handleNestedInputChange(
                    "supplierAgreementRate",
                    "percentage",
                    e.target.value
                  )
                }
                placeholder={"%"}
                error={!!errors["supplierAgreementRate.percentage"]}
                helperText={errors["supplierAgreementRate.percentage"]}
                disabled={disabled}
              />
            </Box>

            <Box sx={{ width: "85%" }}>
              <CustomTextField
                value={formData.supplierAgreementRate.remark}
                onChange={(e) =>
                  handleNestedInputChange(
                    "supplierAgreementRate",
                    "remark",
                    e.target.value
                  )
                }
                error={!!errors["supplierAgreementRate.remark"]}
                helperText={errors["supplierAgreementRate.remark"]}
                disabled={disabled}
              />
            </Box>
          </Box>

          <SectionHeading text="e. What percentage of suppliers identified as having significant actual and potential negative social impacts had their relationships terminated as a result of assessment, and what were the reasons for termination?" />
          <Box
            sx={{
              display: "flex",
              width: "100%",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "15%" }}>
              <CustomTextField
                value={formData.supplierTerminationRate.percentage}
                onChange={(e) =>
                  handleNestedInputChange(
                    "supplierTerminationRate",
                    "percentage",
                    e.target.value
                  )
                }
                placeholder={"%"}
                error={!!errors["supplierTerminationRate.percentage"]}
                helperText={errors["supplierTerminationRate.percentage"]}
                disabled={disabled}
              />
            </Box>

            <Box sx={{ width: "85%" }}>
              <CustomTextField
                value={formData.supplierTerminationRate.remark}
                onChange={(e) =>
                  handleNestedInputChange(
                    "supplierTerminationRate",
                    "remark",
                    e.target.value
                  )
                }
                error={!!errors["supplierTerminationRate.remark"]}
                helperText={errors["supplierTerminationRate.remark"]}
                disabled={disabled}
              />
            </Box>
          </Box>
        </Box>

        {/* Submit Button */}

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackOpen.open}
          message={snackOpen.message}
          autoHideDuration={3000}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          onClose={() => setSnackOpen({ ...snackOpen, open: false })}
        >
          <Alert
            severity={snackOpen.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackOpen.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );

  return (
    <>
      {!hide ? (
        <MainDashboardLayout sidebar={<GRISidebar />}>
          {renderContent({ hide })}

          <>
            {!griReview ? (
              <Box sx={{ ml: "1rem", width: "98%" }}>
                <ReviewBox url={"/gri"} handleSubmit={handleSubmit} />
              </Box>
            ) : (
              <Box textAlign="right" sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{
                    bgcolor: "#1C486B",
                    textTransform: "none",
                    mt: 3,
                    mb: 5,
                  }}
                  onClick={handleSubmit}
                >
                  Save & Continue <EastIcon sx={{ ml: 2 }} />
                </Button>
              </Box>
            )}
          </>
        </MainDashboardLayout>
      ) : (
        renderContent({ hide })
      )}
    </>
  );
};

export default GRI413_1;
